import type {
  AssemblyCurrency,
  MemberAPIResponse,
} from '@assembly-web/services';
import type { MessageDescriptor } from 'react-intl';
import { z } from 'zod';

import { messages } from './message';
import { CustomizeAndStyling } from './TabContent/CustomizeStyling';
import { Details } from './TabContent/Details';
import { Distribution } from './TabContent/Distribution';
import { SettingsAndControls } from './TabContent/SettingsAndControls';

export const getDetailsSchema = (
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, unknown>
  ) => string,
  currency?: AssemblyCurrency
) => {
  return z.object({
    title: z
      .string()
      .min(1, { message: formatMessage(messages.emptyTitle) })
      .max(45, { message: formatMessage(messages.titleTooLongLabel) }),
    description: z
      .string()
      .max(100, { message: formatMessage(messages.descriptionTooLong) })
      .optional(), // update richtext properties
    points: z.coerce
      .number()
      .min(1, {
        message: formatMessage(messages.emptyCurrency, {
          currency:
            currency &&
            currency.name.charAt(0).toUpperCase() + currency.name.slice(1),
        }),
      })
      .max(10000, { message: formatMessage(messages.currencyMax) }),
  });
};

export const getSettingAndControlSchema = (
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, unknown>
  ) => string
) => {
  return z.object({
    totalClaimLimit: z.coerce.number(),
    individualClaimLimit: z.coerce.number().min(1).max(500),
    proof: z.object({
      isRequired: z.boolean(),
      content: z.array(
        z
          .object({
            type: z.string(),
            isChecked: z.boolean().optional(),
            promptText: z.string().max(100, {
              message: formatMessage(messages.promptValidation),
            }),
          })
          .refine(
            (schema) => {
              if (schema.isChecked) {
                return schema.promptText.length > 1;
              }
              return true;
            },
            {
              message: formatMessage(messages.emptyPromptValidation),
              path: ['promptText'],
            }
          )
      ),
    }),
  });
};

export const getCustomizeStylingSchema = (
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, unknown>
  ) => string
) => {
  return z.object({
    image: z.string().optional(),
    claimButtonText: z
      .string()
      .max(20)
      .min(1, { message: formatMessage(messages.emptyClaimButton) }),
    interactionSettings: z.object({
      hideReplies: z.boolean().default(false),
      hideReactions: z.boolean().default(false),
    }),
  });
};

export const distributeSchema = z.object({
  notifyOnEmail: z.boolean(),
  notifyOnMobile: z.boolean(),
});

export const getSchema = (
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, unknown>
  ) => string,
  currency?: AssemblyCurrency
) => {
  return z
    .object({
      launchNow: z.boolean(),
    })
    .merge(getDetailsSchema(formatMessage, currency))
    .merge(getSettingAndControlSchema(formatMessage))
    .merge(getCustomizeStylingSchema(formatMessage))
    .merge(distributeSchema);
};

export const getTabData = (
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, unknown>
  ) => string,
  userDetails: MemberAPIResponse,
  shareChallengeNode: JSX.Element | null
) => {
  return [
    {
      value: 'STEP_1',
      label: formatMessage(messages.detailsLabel),
      component: <Details currency={userDetails.assembly.currency} />,
      schema: getDetailsSchema(formatMessage, userDetails.assembly.currency),
      fields: ['title', 'description', 'points'],
    },
    {
      value: 'STEP_2',
      label: formatMessage(messages.settingsAndControlsLabel),
      component: <SettingsAndControls />,
      schema: getSettingAndControlSchema(formatMessage),
      fields: ['totalClaimLimit', 'individualClaimLimit', 'proof'],
    },
    {
      value: 'STEP_3',
      label: formatMessage(messages.customizeStylingLabel),
      component: <CustomizeAndStyling userDetails={userDetails} />,
      schema: getCustomizeStylingSchema(formatMessage),
      fields: ['claimButtonText'],
    },
    {
      value: 'STEP_4',
      label: formatMessage(messages.distributionLabel),
      component: <Distribution shareChallengeNode={shareChallengeNode} />,
      schema: distributeSchema,
      fields: [],
    },
  ];
};

export const getDefaultValues = (
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, unknown>
  ) => string
) => {
  return {
    individualClaimLimit: 1,
    totalClaimLimit: -1,
    claimButtonText: 'Claim reward',
    interactionSettings: {
      hideReactions: false,
      hideReplies: false,
    },
    proof: {
      isRequired: true,
      content: [
        {
          type: 'TEXT',
          isChecked: true,
          promptText: formatMessage(messages.textPlaceHolder),
        },
        {
          type: 'FILE',
          isChecked: false,
          promptText: '',
        },
      ],
    },
    notifyOnEmail: false,
    notifyOnMobile: false,
  };
};
